import React from 'react';
import { Check, Mail } from 'lucide-react';
import type { PlanDto, PlanId, SubscriptionStatus } from '../../dtos/billing/subscription.dto';
import { BILLING_DISABLED_TITLE, SALES_EMAIL, formatCents, planAction, planActionNeedsBilling } from '../../utils/subscription';

export interface PlanCardsProps {
  plans: PlanDto[];
  currentPlan: PlanId | null;
  status: SubscriptionStatus | null;
  billingEnabled: boolean;
  /** A card is on file (Stripe subscription); decides checkout vs plan change. */
  hasPaymentMethod?: boolean;
  /** A cancellation is scheduled: no plan changes until it is undone. */
  cancelAtPeriodEnd?: boolean;
  /** Holds admin:subscription:manage; others see the catalog without buttons. */
  canManage: boolean;
  /** Plan whose action is in flight (disables every button). */
  busyPlan?: PlanId | null;
  onChoose: (plan: PlanDto) => void;
}

/** "$99 / month" or "Custom pricing". */
export function planPrice(plan: Pick<PlanDto, 'priceCents'>): string {
  return plan.priceCents === null ? 'Custom pricing' : `${formatCents(plan.priceCents)} / month`;
}

/** Phase 16A: the plan's offline line ("Offline mode on phones and tablets"), or null. */
export function planOfflineLine(plan: Pick<PlanDto, 'entitlements'>): string | null {
  return plan.entitlements?.offlineMode === true ? 'Offline mode on phones and tablets' : null;
}

/**
 * Phase 21: what the plan includes in patients, from the API values. Starter: "Up to 10 active
 * patients" (hard cap); Core / Advanced: "Includes 25 active patients, then $9 per patient";
 * a custom plan without terms: "Patient limit set per agency".
 */
export function planUsageLine(plan: Pick<PlanDto, 'patientCap' | 'includedPatients' | 'perPatientCents'>): string {
  const cap = plan.patientCap ?? null;
  const included = plan.includedPatients ?? null;
  const rate = plan.perPatientCents ?? null;
  if (rate !== null) {
    const base = included !== null && included > 0
      ? `Includes ${included} active patients, then ${formatCents(rate)} per patient`
      : `${formatCents(rate)} per active patient`;
    return cap !== null ? `${base}, up to ${cap}` : base;
  }
  return cap === null ? 'Patient limit set per agency' : `Up to ${cap} active patients`;
}

/**
 * Plan catalog from GET /plans (Phase 13, §4.2): monthly prices only, "plus applicable tax",
 * Advanced "Coming soon", and a "Let's talk" card for a custom plan. Buttons follow planAction()
 * (plan §3.3): "Choose" goes to Checkout, "Switch to" is a plan change (a trial switches at once,
 * card or not), and past-due, declined-card or cancel-scheduled accounts get no buttons (the
 * caller shows planActionReason()).
 * Buttons that reach Stripe are disabled for a ledger-only (demo) tenant.
 */
const PlanCards: React.FC<PlanCardsProps> = ({ plans, currentPlan, status, billingEnabled, hasPaymentMethod = false, cancelAtPeriodEnd = false, canManage, busyPlan = null, onChoose }) => {
  const action = status === null ? 'none' : planAction(status, hasPaymentMethod, cancelAtPeriodEnd);
  const checkoutMode = action === 'checkout';
  const blocked = !billingEnabled && status !== null && planActionNeedsBilling(status, hasPaymentMethod);
  const listed = plans.filter((plan) => plan.sellable !== 'contact' && plan.id !== 'custom');

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="plan-cards">
      {listed.map((plan) => {
        const unavailable = plan.comingSoon === true || plan.sellable === false;
        const isCurrent = plan.id === currentPlan;
        const showButton = canManage && action !== 'none' && !unavailable && !(isCurrent && !checkoutMode);
        return (
          <div
            key={plan.id}
            data-testid={`plan-card-${plan.id}`}
            className={`flex flex-col rounded-lg border p-4 text-left ${unavailable ? 'border-slate-200 bg-slate-50 opacity-70' : isCurrent ? 'border-slate-700 ring-1 ring-slate-700' : 'border-slate-200'}`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className={`font-semibold ${unavailable ? 'text-slate-500' : 'text-slate-900'}`}>{plan.name}</span>
              {isCurrent && <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"><Check size={12} aria-hidden="true" />{status === 'trialing' ? 'Trial plan' : 'Current plan'}</span>}
            </div>
            <p className={`mt-2 text-lg font-semibold ${unavailable ? 'text-slate-500' : 'text-slate-900'}`}>{planPrice(plan)}</p>
            <p className="text-xs text-slate-500">plus applicable tax</p>
            <p className="mt-2 text-sm text-slate-600" data-testid={`plan-usage-${plan.id}`}>{planUsageLine(plan)}</p>
            <p className="text-sm text-slate-600">Unlimited users</p>
            {planOfflineLine(plan) && <p className="text-sm text-slate-600">{planOfflineLine(plan)}</p>}
            <div className="mt-auto pt-3">
              {unavailable ? (
                <span className="text-xs font-semibold text-slate-500">Coming soon</span>
              ) : showButton ? (
                <button
                  type="button"
                  onClick={() => onChoose(plan)}
                  disabled={busyPlan !== null || blocked}
                  title={blocked ? BILLING_DISABLED_TITLE : undefined}
                  className="w-full rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busyPlan === plan.id ? 'Opening...' : checkoutMode ? `Choose ${plan.name}` : `Switch to ${plan.name}`}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
      <div data-testid="plan-card-contact" className="flex flex-col rounded-lg border border-slate-200 p-4 text-left">
        <span className="font-semibold text-slate-900">Custom</span>
        <p className="mt-2 text-lg font-semibold text-slate-900">Let&apos;s talk</p>
        <p className="mt-2 text-sm text-slate-600">Larger caseloads, multiple locations or special terms.</p>
        <div className="mt-auto pt-3">
          <a
            href={`mailto:${SALES_EMAIL}`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            <Mail size={16} aria-hidden="true" /> Contact sales
          </a>
        </div>
      </div>
    </div>
  );
};

export default PlanCards;
